import { isProduction } from "@/utils/env-utils";

/** Canonical public origin for attendee-facing links in exports and emails. */
export const DEFAULT_PUBLIC_SITE_URL = "https://www.nowadays.ai";

const LOCALHOST_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]"]);

const isLocalhostOrigin = (url: string): boolean => {
  try {
    return LOCALHOST_HOSTNAMES.has(new URL(url).hostname);
  } catch {
    return false;
  }
};

/**
 * Returns the canonical public site origin for attendee-facing URLs (exports, emails).
 * Uses `NEXT_PUBLIC_SITE_URL` (then `NEXT_PUBLIC_APP_URL`) when set to a non-localhost
 * origin; otherwise falls back to {@link DEFAULT_PUBLIC_SITE_URL}.
 *
 * @returns Absolute origin with no trailing slash.
 */
export const getSiteUrl = (): string => {
  const candidates = [process.env.NEXT_PUBLIC_SITE_URL, process.env.NEXT_PUBLIC_APP_URL].filter(
    (value): value is string => Boolean(value?.trim())
  );

  for (const candidate of candidates) {
    const origin = candidate.replace(/\/+$/, "");
    if (!isLocalhostOrigin(origin)) {
      return origin;
    }
  }

  return DEFAULT_PUBLIC_SITE_URL;
};

/**
 * Builds a public attendee portal dashboard URL.
 *
 * @param slug - Landing page slug.
 * @param token - Attendee access token.
 * @param host - Optional origin override (e.g. `https://anthropic.nowadays.ai`, from
 *   `enterpriseHostFromSlug` in `src/lib/forms/enterprise-host.ts`) for enterprise-branded links.
 *   Defaults to {@link getSiteUrl}.
 * @returns Full dashboard URL.
 */
export const buildAttendeeDashboardUrl = (slug: string, token: string, host: string = getSiteUrl()): string =>
  `${host}/${slug}/attendee/${token}`;

/**
 * Builds the public event landing page URL.
 *
 * @param slug - Landing page slug.
 * @returns Full landing page URL using {@link getSiteUrl}.
 */
export const buildPublicLandingPageUrl = (slug: string): string => `${getSiteUrl()}/${slug}`;

/**
 * Sanitizes a redirect path to prevent open redirects and `javascript:` URL attacks.
 * Only allows relative paths starting with `/`. Returns the fallback for any absolute,
 * protocol-relative, or scheme URLs.
 *
 * @param redirectTo - The user-provided redirect target.
 * @param fallback - The safe default path if the target is invalid.
 * @returns A safe internal path.
 */
export const getSafeRedirectUrl = (redirectTo: string | undefined, fallback = "/dashboard"): string => {
  if (!redirectTo) return fallback;

  // Only allow paths that start with a single `/` and not `//` (protocol-relative)
  if (!redirectTo.startsWith("/") || redirectTo.startsWith("//")) return fallback;

  // Block any scheme-like patterns (e.g., `javascript:`, `data:`, `vbscript:`)
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(redirectTo)) return fallback;

  return redirectTo;
};

/**
 * Gets the base URL for the application based on the current environment.
 * Returns the current origin if available (for dynamic routing), otherwise falls back to env variable.
 * On server-side, attempts to use the request hostname to preserve subdomain (e.g., stova.nowadays.ai).
 */
export const getBaseUrl = async () => {
  // In browser, use current origin to avoid www/non-www mismatches
  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  // Server-side: try to get hostname from request headers to preserve subdomain
  try {
    // Dynamically import headers only on server-side to avoid client bundle issues
    const { headers } = await import("next/headers");
    const headersList = await headers();
    const hostname = headersList.get("host");
    if (hostname) {
      const protocol = isProduction ? "https" : "http";
      return `${protocol}://${hostname}`;
    }
  } catch {}

  // Fallback to env variable
  return process.env.NEXT_PUBLIC_SITE_URL;
};

/**
 * Trusted base origin for server-to-server calls back into this same
 * deployment. Unlike {@link getBaseUrl}, this never derives the origin from the
 * request `Host` header (which an attacker can spoof to point a self-`fetch` at
 * an arbitrary target — SSRF). Resolves to Vercel's injected deployment URL,
 * then the configured site URL, then localhost for local dev.
 *
 * @returns A trusted absolute origin with no trailing slash.
 */
export const getInternalBaseUrl = (): string => {
  const origin = process.env.VERCEL_URL
    ? `https://${process.env.VERCEL_URL}`
    : process.env.NEXT_PUBLIC_SITE_URL || "http://localhost:3000";
  // Honor the documented "no trailing slash" contract so callers can safely
  // append `/api/...` without producing `//api/...`.
  return origin.replace(/\/+$/, "");
};

/**
 * Builds an absolute deep link into the Fetch v2 admin workspace for one event, optionally
 * pre-selecting a venue. This is the single source of truth for the route + query contract the
 * missing-contact Slack cards link with — the `event` / `venue` param names are parsed back by
 * `useWorkspaceNav` (`?venue=` carries the venue's `venue_static_id`), so keeping the string in one
 * place stops the two builders and the hook from drifting apart. Uses {@link getInternalBaseUrl} (the
 * SSRF-safe server origin, never the request Host header).
 *
 * @param eventId - The event to open.
 * @param venueStaticId - Optional venue_static_id to pre-select; omitted/null stays event-scoped
 *   (e.g. a card reporting several venues has no single target).
 * @returns Absolute URL, e.g. `https://…/admin/fetch-v2?event=42&venue=396434`.
 */
export const fetchVenueDeepLink = (eventId: number, venueStaticId?: number | null): string => {
  const base = `${getInternalBaseUrl()}/admin/fetch-v2?event=${eventId}`;
  return venueStaticId != null ? `${base}&venue=${venueStaticId}` : base;
};

/**
 * Generates canonical URLs for publications based on type and slug.
 */
export const generateCanonicalUrl = (
  type: "blog" | "case-study" | "competitor" | "changelog",
  slug: string
): string => {
  const pathMap = {
    blog: "blog",
    "case-study": "case-studies",
    competitor: "competitor",
    changelog: "changelog",
  };

  return `https://nowadays.ai/${pathMap[type]}/${slug}`;
};

/**
 * Constructs a full URL by combining the base URL with a path.
 * Works synchronously in browser, asynchronously on server.
 *
 * @param path The path to append to the base URL
 * @returns The full URL (Promise on server, string in browser)
 */
export const getFullUrl = (path: string): string | Promise<string> => {
  if (typeof window !== "undefined") {
    const baseUrl = window.location.origin;
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${baseUrl}/${cleanPath}`;
  }

  return getBaseUrl().then((baseUrl) => {
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${baseUrl}/${cleanPath}`;
  });
};

/**
 * Gets the base URL for the Nowadays API.
 *
 * @param path The path to append to the base URL.
 * @returns The base URL for the Nowadays API
 */
export const nowadaysApiUrl = (path: string) => {
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  // Uncomment this to use the local API
  // return "http://localhost:8000" + path;

  const baseUrl = isProduction ? "https://api.nowadays.ai" : "https://api-staging.nowadays.ai";
  return `${baseUrl}${path}`;
};

/** Max attempts to download a single image before giving up. */
const IMAGE_DOWNLOAD_MAX_ATTEMPTS = 3;
/** Base backoff (ms) between download retries; grows exponentially per attempt. */
const IMAGE_DOWNLOAD_RETRY_BASE_MS = 300;
/** How many images to download concurrently to avoid bursting storage rate limits. */
const IMAGE_DOWNLOAD_BATCH_SIZE = 6;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Whether a URL is a plain `https:` URL safe to fetch and re-upload.
 *
 * Image URLs reaching these helpers come from an external scrape backend and
 * stored DB columns, so anything that isn't `https:` (e.g. `data:`, `blob:`,
 * `file:`) is rejected before it can be fetched and turned into a File.
 *
 * @param url The URL to check.
 * @returns True if the URL parses and uses the `https:` protocol.
 */
const isHttpsUrl = (url: string): boolean => {
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * Whether a hostname or IP-literal string is a loopback/private/link-local/internal address —
 * pure string/number parsing, no DNS or Node APIs, so it's safe to use from both the browser
 * and the server. Shared by {@link isPubliclyRoutableHttpsUrl} (checks a URL's literal
 * hostname) and, server-side only, a DNS-resolution check that closes the gap this alone
 * can't: a hostname string that isn't itself a private IP can still *resolve* to one (DNS
 * rebinding) — this function is reused there against the resolved addresses, not just the
 * literal hostname.
 *
 * @param host A hostname or IP-address string (literal or DNS-resolved).
 * @returns True if the host is loopback, private, link-local, carrier-grade-NAT, or internal-looking.
 */
export const isPrivateOrInternalHost = (host: string): boolean => {
  const hostname = host.toLowerCase();
  if (hostname === "localhost" || hostname.endsWith(".local") || hostname.endsWith(".internal")) return true;

  const ipv4 = hostname.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (ipv4) {
    const [a, b] = [Number(ipv4[1]), Number(ipv4[2])];
    if (a === 127 || a === 10 || a === 0) return true; // loopback / private / "this" network
    if (a === 169 && b === 254) return true; // link-local, incl. cloud metadata (169.254.169.254)
    if (a === 172 && b >= 16 && b <= 31) return true; // private
    if (a === 192 && b === 168) return true; // private
    if (a === 100 && b >= 64 && b <= 127) return true; // carrier-grade NAT
  }

  if (hostname === "::1" || hostname.startsWith("fe80:") || hostname.startsWith("fc") || hostname.startsWith("fd")) {
    return true; // IPv6 loopback / link-local / unique-local
  }

  return false;
};

/**
 * Whether a URL is safe to fetch server-side without exposing an SSRF vector — an `https:`
 * URL whose *literal* hostname isn't a loopback/private/link-local address or internal name.
 *
 * `downloadImageWithRetry` fetches these URLs from the server (a Server Action, not the
 * browser), and at least one caller (`saveVenueImages`) passes fully client-supplied URLs
 * straight through — without this, an authenticated caller could point the download at
 * internal infrastructure (cloud metadata endpoints, private-network services) instead of a
 * real image host. This is a hostname/IP-literal pattern check, not a DNS resolution — a
 * hostname that only *resolves* to a private address at request time (DNS rebinding) isn't
 * caught here; `saveVenueImages` additionally resolves and re-checks each hostname
 * server-side (`node:dns`, which can't live in this shared client+server file) before this
 * function's caller ever fetches.
 *
 * @param url The URL to check.
 * @returns True if the URL parses, uses `https:`, and its literal host isn't private/internal.
 */
const isPubliclyRoutableHttpsUrl = (url: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== "https:") return false;

  return !isPrivateOrInternalHost(parsed.hostname);
};

/**
 * Downloads a single image URL into a File, retrying transient failures.
 *
 * Treats network errors, 429s, and 5xx responses as transient and retries with
 * exponential backoff. A non-ok 4xx (other than 429) is permanent and returns
 * null immediately so error-page bodies are never turned into junk image Files.
 *
 * Redirects are never followed — an unvalidated redirect (`fetch`'s default behavior) could
 * point at an internal host past the {@link isPubliclyRoutableHttpsUrl} check on the
 * original URL, so a redirect response is treated as a permanent failure instead.
 *
 * @param url The image URL to download.
 * @returns The downloaded File, or null if it could not be retrieved.
 */
const downloadImageWithRetry = async (url: string): Promise<File | null> => {
  if (!isPubliclyRoutableHttpsUrl(url)) return null;

  for (let attempt = 0; attempt < IMAGE_DOWNLOAD_MAX_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(url, { redirect: "manual" });

      if (response.type === "opaqueredirect" || (response.status >= 300 && response.status < 400)) {
        return null;
      }

      if (!response.ok) {
        // Permanent client errors won't recover on retry; bail out early.
        if (response.status !== 429 && response.status < 500) return null;
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const filename = url.split("/").pop() || "image.jpg";
      return new File([blob], filename, { type: blob.type });
    } catch {
      if (attempt < IMAGE_DOWNLOAD_MAX_ATTEMPTS - 1) {
        await wait(IMAGE_DOWNLOAD_RETRY_BASE_MS * 2 ** attempt);
      }
    }
  }

  return null;
};

/**
 * Downloads images from URLs and returns them as File objects.
 *
 * Runs in bounded-concurrency batches with per-image retries so a burst of
 * downloads doesn't trip storage rate limits and silently drop images. Only
 * successfully downloaded files are returned; unreachable URLs are skipped.
 *
 * @param imageUrls Array of image URLs to download
 * @returns Promise resolving to array of downloaded Files
 */
export const downloadImagesFromUrls = async (imageUrls: string[]): Promise<File[]> => {
  const downloadedFiles: File[] = [];

  for (let i = 0; i < imageUrls.length; i += IMAGE_DOWNLOAD_BATCH_SIZE) {
    const batch = imageUrls.slice(i, i + IMAGE_DOWNLOAD_BATCH_SIZE);
    const files = await Promise.all(batch.map(downloadImageWithRetry));
    downloadedFiles.push(...files.filter((file): file is File => file !== null));
  }

  return downloadedFiles;
};

/**
 * Like {@link downloadImagesFromUrls}, but preserves each result's original array position
 * (a failed download becomes `null` rather than being dropped) — for callers that need to
 * map an uploaded file back to which source URL it came from, e.g. finding which uploaded
 * image corresponds to a selected cover photo when some downloads fail.
 *
 * @param imageUrls Array of image URLs to download
 * @returns Promise resolving to an array the same length as `imageUrls`, `null` for failures
 */
export const downloadImagesFromUrlsPreservingOrder = async (imageUrls: string[]): Promise<(File | null)[]> => {
  const results: (File | null)[] = new Array(imageUrls.length).fill(null);

  for (let i = 0; i < imageUrls.length; i += IMAGE_DOWNLOAD_BATCH_SIZE) {
    const batch = imageUrls.slice(i, i + IMAGE_DOWNLOAD_BATCH_SIZE);
    const files = await Promise.all(batch.map(downloadImageWithRetry));
    files.forEach((file, batchIndex) => {
      results[i + batchIndex] = file;
    });
  }

  return results;
};

/**
 * Checks an original image URL and falls back to backup URLs if needed.
 *
 * @param originalUrl The primary image URL to check first
 * @param backupUrls Array of backup image URLs to try if original fails
 * @returns Promise resolving to either working URL or original URL if all fail
 */
export const getWorkingImageUrl = async (
  originalUrl: string | undefined,
  backupUrls: string[]
): Promise<string | null> => {
  // Helper function to check if URL returns valid response
  const isUrlValid = async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url);
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  // First check original URL
  if (originalUrl && (await isUrlValid(originalUrl))) {
    return originalUrl;
  }

  // Try each backup URL in order
  for (const backupUrl of backupUrls) {
    if (await isUrlValid(backupUrl)) {
      return backupUrl;
    }
  }

  // If all URLs fail, return original
  return originalUrl ?? null;
};

/** How many reachability checks to run concurrently to avoid flooding the network. */
const IMAGE_REACHABILITY_BATCH_SIZE = 8;
/**
 * Max time (ms) to wait for the `<img>`-based fallback probe before giving up.
 * Short, since this only runs for images that are expected to render fine in
 * the grid (a working image typically fires `onload` in well under a second);
 * a long timeout here would let one hung URL stall an entire reachability batch.
 */
const IMAGE_LOAD_PROBE_TIMEOUT_MS = 3000;

/**
 * Loads a URL as an image and resolves whether it loads successfully.
 *
 * Unlike `fetch()`, loading an `<img>` cross-origin is not gated by CORS —
 * only reading its pixel data (e.g. via canvas) would be. Used as a fallback
 * for hosts that don't send permissive CORS headers on a `fetch()`, which is
 * common for third-party image hosts (Google Maps, TripAdvisor, hotel sites)
 * even though the image itself loads fine in the grid's `<img>` tags.
 *
 * No-ops to `false` outside a browser (no `Image` constructor) instead of
 * throwing, matching the fail-safe behavior of the `fetch()` path it backs up.
 *
 * @param url The image URL to check.
 * @returns True if the image loaded successfully before the timeout.
 */
const isImageUrlLoadable = (url: string): Promise<boolean> => {
  if (typeof Image === "undefined") return Promise.resolve(false);

  return new Promise((resolve) => {
    const img = new Image();
    const timeout = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      // Abort the in-flight request rather than leaving it to finish in the
      // background — matters when a batch of hung third-party URLs times out.
      img.src = "";
      resolve(false);
    }, IMAGE_LOAD_PROBE_TIMEOUT_MS);

    img.onload = () => {
      clearTimeout(timeout);
      resolve(true);
    };
    img.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };
    img.src = url;
  });
};

/**
 * Checks whether a single image URL is reachable.
 *
 * Prefers a lightweight HEAD request, but some hosts/CDNs reject HEAD (403/405/501)
 * while serving GET fine, so those statuses fall back to a ranged GET (one byte) to
 * avoid wrongly dropping a reachable image. Non-https URLs are treated as unreachable.
 *
 * @param url The image URL to check.
 * @returns True if the URL responded successfully.
 */
const isImageUrlReachable = async (url: string): Promise<boolean> => {
  if (!isHttpsUrl(url)) return false;

  try {
    const head = await fetch(url, { method: "HEAD" });
    if (head.ok) return true;

    // HEAD blocked (not "missing") — confirm with a minimal ranged GET.
    if (head.status === 403 || head.status === 405 || head.status === 501) {
      const get = await fetch(url, { headers: { Range: "bytes=0-0" } });
      return get.ok;
    }

    return false;
  } catch {
    // fetch() throwing (rather than resolving with a non-ok status) is the
    // signature of a CORS-blocked cross-origin request, not necessarily a dead
    // URL. Fall back to an <img>-based load probe, which isn't subject to CORS
    // the same way, to avoid wrongly dropping images that render fine.
    return isImageUrlLoadable(url);
  }
};

/**
 * Filters a list of image URLs down to those that are actually reachable.
 *
 * Runs reachability checks in batches (to avoid flooding the network) and keeps
 * only the URLs that respond successfully. Used to guard against backends that
 * return storage URLs for objects that were never uploaded, which would otherwise
 * render as broken thumbnails and be silently dropped on save.
 *
 * @param urls The image URLs to check.
 * @param batchSize How many checks to run concurrently (defaults to {@link IMAGE_REACHABILITY_BATCH_SIZE}).
 * @returns The subset of `urls` that responded successfully, in original order.
 */
export const filterReachableImageUrls = async (
  urls: string[],
  batchSize: number = IMAGE_REACHABILITY_BATCH_SIZE
): Promise<string[]> => {
  const reachable: string[] = [];

  for (let i = 0; i < urls.length; i += batchSize) {
    const batch = urls.slice(i, i + batchSize);
    const results = await Promise.all(batch.map(async (url) => ((await isImageUrlReachable(url)) ? url : null)));
    reachable.push(...results.filter((url): url is string => url !== null));
  }

  return reachable;
};

/**
 * Parses a string representation of URLs into an array of URLs.
 * Handles both formats:
 * - Array of direct URLs: ["https://example.com/image.jpg"]
 * - Array of objects with URL property: [{"url": "https://example.com/image.jpg"}]
 *
 * @param urlString The string to parse
 * @returns Array of parsed URLs
 */
export const parseUrlString = (urlString: string): string[] => {
  try {
    const parsed = JSON.parse(urlString);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (typeof item === "object" && item !== null && "url" in item) {
          return item.url;
        }
        return null;
      })
      .filter((url): url is string => url !== null);
  } catch (error) {
    return [];
  }
};

/**
 * Gets the base URL for the AVA API.
 *
 * @param path The path to append to the base URL.
 * @returns The base URL for the AVA API
 */
export const avaApiUrl = (path: string) => {
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  const baseUrl = process.env.NEXT_PUBLIC_AVA_API_URL || "https://automation.nowadays.ai/ava-orchestrator/api/v1";
  return baseUrl + path;
};

/**
 * Gets the base URL for the EMA API.
 *
 * @param path The path to append to the base URL.
 * @returns The base URL for the EMA API
 */
export const emaApiUrl = (path: string) => {
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  const baseUrl = process.env.NEXT_PUBLIC_EMA_API_URL || "https://automation.nowadays.ai/ema/api";
  return baseUrl + path;
};

/**
 * Gets the base URL for the OEE API.
 *
 * @param path The path to append to the base URL.
 * @returns The base URL for the OEE API
 */
export const oeeApiUrl = (path: string) => {
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  const baseUrl =
    process.env.NEXT_PUBLIC_OEE_API_URL || process.env.OEE_API_URL || "https://automation.nowadays.ai/oee/api";
  return baseUrl + path;
};

/**
 * Hosts permitted for the ZED backend in production. A misconfigured
 * `NEXT_PUBLIC_ZED_API_URL` outside this list is rejected at module load.
 */
const ZED_ALLOWED_HOSTS = new Set(["automation.nowadays.ai", "localhost"]);
const ZED_DEFAULT_BASE = "https://automation.nowadays.ai/zed";

/** Resolved ZED base URL — env override validated at module load time. */
const ZED_BASE_URL: string = (() => {
  const env = process.env.NEXT_PUBLIC_ZED_API_URL;
  if (!env) return ZED_DEFAULT_BASE;
  try {
    const url = new URL(env);
    if (isProduction && !ZED_ALLOWED_HOSTS.has(url.hostname)) {
      // Refuse to send bearer tokens to an unrecognized host in prod.
      console.error(`zedApiUrl: refusing unrecognized host '${url.hostname}' in production; falling back to default`);
      return ZED_DEFAULT_BASE;
    }
    return env;
  } catch {
    console.error(`zedApiUrl: malformed NEXT_PUBLIC_ZED_API_URL='${env}'; falling back to default`);
    return ZED_DEFAULT_BASE;
  }
})();

/**
 * Gets the URL for a ZED API path. Validates the path is a clean relative
 * reference so a caller-controlled value (e.g. an interpolated thread id)
 * can't escape the base origin.
 *
 * Set `NEXT_PUBLIC_ZED_API_URL=http://localhost:8006/zed` in `.env.local` for
 * local development.
 *
 * @param path A path beginning with `/`, e.g. `/threads/{id}/messages`.
 * @returns The fully-qualified URL.
 * @throws If `path` looks like an absolute URL, a scheme reference, or a
 *   protocol-relative URL that could redirect bearer tokens off-origin.
 */
export const zedApiUrl = (path: string): string => {
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  // Reject scheme-prefixed (`/javascript:...`) and protocol-relative (`//host/...`) paths.
  if (path.startsWith("//") || /^\/[a-zA-Z][a-zA-Z\d+\-.]*:/.test(path)) {
    throw new Error(`zedApiUrl: path '${path}' is not a safe relative path`);
  }
  return ZED_BASE_URL + path;
};
